import Vue from 'vue';
import VueI18n from 'vue-i18n';
import { TranslationsService } from '@/core/services';

Vue.use(VueI18n);

export let i18n = null;

const DEFAULT_LOCALE = process.env.VUE_APP_I18N_LOCALE || 'en-US';
const FALLBACK_LOCALE = process.env.VUE_APP_I18N_FALLBACK_LOCALE || 'en-US';

/**
 * Builds a language code map from available languages
 * @param {Array} languages - Array of languages from the service
 * @returns {Object} Map from base code to full culture_name
 * @example
 * Input: [{ culture_name: 'en-US' }, { culture_name: 'es' }, { culture_name: 'fr-FR' }]
 * Output: { en: 'en-US', es: 'es', fr: 'fr-FR' }
 */
const buildLanguageMap = (languages) => {
  const map = {};

  languages.forEach((language) => {
    const cultureName = language.culture_name;
    // Extract base code (e.g., 'en' from 'en-US')
    const langCode = cultureName.split('-')[0].toLowerCase();

    // Map base code to full culture_name
    // If there are multiple variants of the same language, keep the first one found
    if (!map[langCode]) {
      map[langCode] = cultureName;
    }
  });

  return map;
};

/**
 * Detects the browser language and maps it to a supported locale
 * @param {Object} languageMap - Map of available language codes
 * @returns {string} Normalized locale (e.g., 'en-US' or 'es')
 */
const detectBrowserLocale = (languageMap) => {
  // Get browser language
  const browserLang =
    navigator.language || navigator.userLanguage || DEFAULT_LOCALE;

  // Extract base language code (e.g., 'en' from 'en-GB')
  const langCode = browserLang.split('-')[0].toLowerCase();

  // Map to a supported locale
  return languageMap[langCode] || DEFAULT_LOCALE;
};

/**
 * Determines the initial locale based on browser detection
 * @param {Object} languageMap - Map of available language codes
 * @returns {string} Locale to use
 */
const getInitialLocale = (languageMap) => {
  // Detect browser language (falls back to DEFAULT_LOCALE if not supported)
  return detectBrowserLocale(languageMap);
};

/**
 * Changes the application language dynamically
 * @param {string} newLocale - Language code (e.g., 'en-US', 'es')
 */
export const setLocale = (newLocale) => {
  if (!i18n) {
    // eslint-disable-next-line
    console.warn('i18n not initialized yet');
    return;
  }

  const normalizedLocale = newLocale || DEFAULT_LOCALE;

  if (i18n.availableLocales.includes(normalizedLocale)) {
    i18n.locale = normalizedLocale;
    // Save preference in localStorage for persistence
    localStorage.setItem('user-locale', normalizedLocale);
    // eslint-disable-next-line
    console.log('Locale changed to:', normalizedLocale);
  } else {
    // eslint-disable-next-line
    console.warn(
      `Locale "${normalizedLocale}" not available. Available:`,
      i18n.availableLocales,
    );
  }
};

/**
 * Gets the user's saved locale or the initial one
 * @param {Object} languageMap - Map of available language codes
 * @returns {string} Locale to use
 */
const getSavedOrInitialLocale = (languageMap) => {
  // Check if there's a saved preference
  const savedLocale = localStorage.getItem('user-locale');
  if (savedLocale) {
    return savedLocale;
  }

  return getInitialLocale(languageMap);
};

const initI18n = async () => {
  const fallbackLocale = FALLBACK_LOCALE;

  try {
    const languages = await TranslationsService.getLanguages();

    if (!languages || languages.length === 0) {
      throw new Error('No languages available from translation service');
    }

    // Build language map dynamically from the service
    const languageMap = buildLanguageMap(languages);
    // eslint-disable-next-line
    console.log('Dynamic language map built:', languageMap);

    // Determine locale using the dynamic map
    const locale = getSavedOrInitialLocale(languageMap);

    const messages = {};
    const loadedLanguages = [];

    // Load translations for each available language
    for (const language of languages) {
      try {
        messages[language.culture_name] =
          await TranslationsService.getTranslations(language.culture_name);
        loadedLanguages.push(language.culture_name);
      } catch (error) {
        // eslint-disable-next-line
        console.warn(
          `Failed to load translations for ${language.culture_name}:`,
          error,
        );
      }
    }

    // Validate that at least one language has been loaded
    if (Object.keys(messages).length === 0) {
      throw new Error('No translations could be loaded from any language');
    }

    // Extract base codes from loaded languages (en, es)
    const loadedBaseLangs = loadedLanguages.map((lang) =>
      lang.split('-')[0].toLowerCase(),
    );

    // Validate that we have English (required as fallback)
    if (!loadedBaseLangs.includes('en')) {
      // eslint-disable-next-line
      console.error(
        'English translations are required but not available!',
        'Loaded languages:',
        loadedLanguages,
      );
    }

    // Verify if the selected locale is available
    let finalLocale = locale;
    if (!loadedLanguages.includes(locale)) {
      // eslint-disable-next-line
      console.warn(
        `Preferred locale "${locale}" not available. Available:`,
        loadedLanguages,
      );

      // Try to use the base code (e.g., if 'en-US' is not available, find any 'en-*')
      const langCode = locale.split('-')[0].toLowerCase();
      const alternativeLocale = loadedLanguages.find((lang) =>
        lang.toLowerCase().startsWith(langCode),
      );

      if (alternativeLocale) {
        finalLocale = alternativeLocale;
        // eslint-disable-next-line
        console.warn(`Using alternative locale: ${alternativeLocale}`);
      } else {
        finalLocale = fallbackLocale;
        // eslint-disable-next-line
        console.warn(`Falling back to: ${fallbackLocale}`);
      }
    }

    // eslint-disable-next-line
    console.log('i18n initialized successfully');
    // eslint-disable-next-line
    console.log('Active locale:', finalLocale);
    // eslint-disable-next-line
    console.log('Fallback locale:', fallbackLocale);
    // eslint-disable-next-line
    console.log('Loaded languages:', loadedLanguages);

    console.log('Loaded messages:', messages);

    // Vue 2 syntax - create VueI18n instance
    i18n = new VueI18n({
      locale: finalLocale,
      fallbackLocale,
      messages,
    });
  } catch (e) {
    // eslint-disable-next-line
    console.error('Failed to initialize i18n:', e);
    throw new Error(
      'No connection and no local storage translations available to load. ' +
        `Original error: ${e.message || e}`,
    );
  }

  return i18n;
};

export { initI18n };
